import { useMemo, useState } from 'react'
import { Box, Button, Grid, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material'
import { SectionLoader } from 'Components/Section'
import { useKoreanLanguage2 } from '../Hooks/useKoreanLanguageForm'

type RegionItem = {
  regionCode: number
  regionName: string
}

type CreateSchoolPayload = {
  regionCode: number
  schoolNameUz: string
  schoolNameRu: string
  schoolNameKo: string
  login?: string | null
  password?: string | null
}

type Props = {
  onCancel: () => void
}

const initialForm: CreateSchoolPayload = {
  regionCode: 0,
  schoolNameUz: '',
  schoolNameRu: '',
  schoolNameKo: '',
  login: '',
  password: '',
}

type TouchedFields = {
  regionCode: boolean
  schoolNameUz: boolean
  schoolNameRu: boolean
  schoolNameKo: boolean
}

const initialTouched: TouchedFields = {
  regionCode: false,
  schoolNameUz: false,
  schoolNameRu: false,
  schoolNameKo: false,
}

export const KoreanLanguageSchoolCreateForm = ({ onCancel }: Props) => {
  const [form, setForm] = useState<CreateSchoolPayload>(initialForm)
  const [touched, setTouched] = useState<TouchedFields>(initialTouched)
  const [submitAttempted, setSubmitAttempted] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [submitSuccess, setSubmitSuccess] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { regions, addNewSchoolMutation } = useKoreanLanguage2()

  const rawErrors = useMemo(() => {
    return {
      regionCode: !form.regionCode,
      schoolNameUz: !form.schoolNameUz.trim(),
      schoolNameRu: !form.schoolNameRu.trim(),
      schoolNameKo: !form.schoolNameKo.trim(),
    }
  }, [form])

  const visibleErrors = {
    regionCode: rawErrors.regionCode && (touched.regionCode || submitAttempted),
    schoolNameUz: rawErrors.schoolNameUz && (touched.schoolNameUz || submitAttempted),
    schoolNameRu: rawErrors.schoolNameRu && (touched.schoolNameRu || submitAttempted),
    schoolNameKo: rawErrors.schoolNameKo && (touched.schoolNameKo || submitAttempted),
  }

  const isDisabled = rawErrors.regionCode || rawErrors.schoolNameUz || rawErrors.schoolNameRu || rawErrors.schoolNameKo || isSubmitting

  const handleChange = (field: keyof CreateSchoolPayload) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value

    setForm(prev => ({
      ...prev,
      [field]: field === 'regionCode' ? Number(value) : value,
    }))

    if (submitError) setSubmitError('')
    if (submitSuccess) setSubmitSuccess('')
  }

  const handleBlur = (field: keyof TouchedFields) => () => {
    setTouched(prev => ({
      ...prev,
      [field]: true,
    }))
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSubmitAttempted(true)

    if (rawErrors.regionCode || rawErrors.schoolNameUz || rawErrors.schoolNameRu || rawErrors.schoolNameKo) {
      return
    }

    try {
      setIsSubmitting(true)
      setSubmitError('')
      setSubmitSuccess('')

      addNewSchoolMutation({
        regionCode: form.regionCode,
        schoolNameUz: form.schoolNameUz.trim(),
        schoolNameRu: form.schoolNameRu.trim(),
        schoolNameKo: form.schoolNameKo.trim(),
        login: form.login?.trim() || null,
        password: form.password?.trim() || null,
      })

      setSubmitSuccess('Школа успешно создана.')
      setForm(initialForm)
      setTouched(initialTouched)
      setSubmitAttempted(false)
    } catch (error: any) {
      setSubmitError(error?.response?.data?.message || 'Произошла ошибка при создании школы. Пожалуйста, попробуйте снова.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <SectionLoader isLoading={false} isFetching={false}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: '#fff',
        }}>
        <Stack spacing={3} component='form' onSubmit={handleSubmit}>
          <Box>
            <Typography variant='h5' fontWeight={700}>
              Добавить новую школу
            </Typography>
            <Typography variant='body2' color='text.secondary' mt={0.5}>
              Выберите регион и введите название школы на трех языках.
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label='Выберите регион'
                value={form.regionCode || ''}
                onChange={handleChange('regionCode')}
                onBlur={handleBlur('regionCode')}
                error={visibleErrors.regionCode}
                helperText={visibleErrors.regionCode ? 'Пожалуйста, выберите регион.' : ' '}>
                {(regions || []).map((region: RegionItem) => (
                  <MenuItem key={region.regionCode} value={region.regionCode}>
                    {region.regionName}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6} />

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label='Название школы (UZ)'
                value={form.schoolNameUz}
                onChange={handleChange('schoolNameUz')}
                onBlur={handleBlur('schoolNameUz')}
                error={visibleErrors.schoolNameUz}
                helperText={visibleErrors.schoolNameUz ? 'Пожалуйста, введите название школы на узбекском языке.' : ' '}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label='Название школы (RU)'
                value={form.schoolNameRu}
                onChange={handleChange('schoolNameRu')}
                onBlur={handleBlur('schoolNameRu')}
                error={visibleErrors.schoolNameRu}
                helperText={visibleErrors.schoolNameRu ? 'Пожалуйста, введите название школы на русском языке.' : ' '}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label='Название школы (KO)'
                value={form.schoolNameKo}
                onChange={handleChange('schoolNameKo')}
                onBlur={handleBlur('schoolNameKo')}
                error={visibleErrors.schoolNameKo}
                helperText={visibleErrors.schoolNameKo ? 'Пожалуйста, введите название школы на корейском языке.' : ' '}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label='Login' value={form.login ?? ''} onChange={handleChange('login')} helperText=' ' />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label='Password'
                type='password'
                value={form.password ?? ''}
                onChange={handleChange('password')}
                helperText=' '
              />
            </Grid>
          </Grid>

          <Stack direction='row' spacing={1.5} justifyContent='flex-end'>
            <Button variant='outlined' color='inherit' onClick={onCancel} disabled={isSubmitting}>
              Отмена
            </Button>
            <Button type='submit' variant='contained' disabled={isDisabled}>
              {isSubmitting ? 'Сохранение...' : 'Сохранить'}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </SectionLoader>
  )
}
